import React, { useEffect, useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { editGroup, editNameGroup, getAllGroup, getAllUserByRole, saveGroup } from "../api/appliApi";
import { format } from "date-fns";
import fr from "date-fns/locale/fr";
import FilterComponent from "../components/FilterComponent";
import CustomLoader from "../components/LoaderComponent";
import { customHeader, paginationOptions } from "../utils/StyledDataTable";
import { Form } from "react-bootstrap";
import { FormModal } from "../components/FormModal";
import { ToastContainer, toast } from "react-toastify";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import { TableContainer } from "../utils/StyledComponents";
import { ConditionalStyles } from "react-data-table-component";
import plusCircle from "../asset/img/icon/plus-circle.svg";
import { FormElement, UserData, UserGroupInterface } from "../utils/Type";
import { MdEdit } from "react-icons/md";

const clearFormData = () => ({
    id: "",
    name: "",
    admin_id: [] as string[],
    adminName: "",
    codeName: ""
});

const UserGroup = () => {
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [pending, setPending] = useState(true);
    const [datas, setDatas] = useState<UserGroupInterface[]>([]);
    const [modalShow, setModalShow] = React.useState(false);
    const [modalUpdateShow, setModalUpdateShow] = React.useState(false);
    const [userList, setUserList] = useState<UserData[]>([]);
    const [formData, setFormData] = useState(clearFormData());
    const submitLockRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);

    const fetchData = async () => {
        try {
            const data = await getAllGroup();
            if (Array.isArray(data.data)) {
                setDatas(data.data);
            } else {
                setDatas([]);
            }
        } catch (error) {
            setDatas([]);
        } finally {
            setPending(false);
        }
    };

    useEffect(() => {
        const getDataUser = async () => {
            try {
                const data = await getAllUserByRole("admin");
                setUserList(data.data);
            } catch (error) {
                setUserList([]);
            }
        }
        fetchData();
        getDataUser();
    }, []);

    const filteredData = useMemo(() => {
        const txt = filterText.toLowerCase();
        return datas.filter(d =>
            d.id.toString().includes(txt) ||
            d.name.toLowerCase().includes(txt) ||
            d.adminName.toLowerCase().includes(txt) ||
            d.codeName.toLowerCase().includes(txt)
        );
    }, [filterText, datas]);

    const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterText(event.target.value);
    };

    const columns = [
        {
            name: 'Id',
            selector: (row: { id: string }) => row.id,
            sortable: true,
            width: '7%'
        },
        {
            name: 'Nom',
            selector: (row: { name: string }) => row.name,
            sortable: true,
        },
        {
            name: 'Code',
            selector: (row: { codeName: string }) => row.codeName,
            sortable: true,
        },
        {
            name: 'Administrateur',
            selector: (row: { adminName: string }) => row.adminName,
            width: '30%',
            sortable: true,
        },
        {
            name: 'Date de création',
            selector: (row: UserGroupInterface) => format(new Date(row.createdAt), "dd/MM/yyyy HH:mm", { locale: fr }),
            sortable: true,
        },
        {
            name: 'Date de modification',
            selector: (row: UserGroupInterface) => format(new Date(row.updatedAt), "dd/MM/yyyy HH:mm", { locale: fr }),
            sortable: true,
        },
        {
            name: 'Action',
            cell: (row: any) => (
                <>
                    <MdEdit size={24} onClick={() => handleEdit(row)} style={{ cursor: "pointer" }} />
                </>
            ),
            ignoreRowClick: true
        }
    ];

    const conditionalRowStyles: ConditionalStyles<UserGroupInterface>[] = [
        {
            when: row => filteredData.indexOf(row) % 2 !== 0,
            style: { backgroundColor: "#F6F8FD" },
        },
    ];

    const handleClear = () => {
        if (filterText) {
            setResetPaginationToggle(!resetPaginationToggle);
            setFilterText('');
        }
    };

    const handleChange = (e: React.ChangeEvent<FormElement>) => {
        const { name, value, type } = e.target;
        if (type === "select-multiple") {
            const options = (e.target as HTMLSelectElement).selectedOptions;
            const values = Array.from(options).map(option => option.value);
            setFormData({
                ...formData,
                [name]: values
            });
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    }

    const notify = (message: string) => toast(message);
    const notifyError = (message: string) => toast.error(message);

    const handleValidate = async () => {
        if (submitLockRef.current || isSaving) return;

        submitLockRef.current = true;
        setIsSaving(true);

        try {
            if (!formData.name.trim()) {
                notifyError("Le nom est requis.");
                return;
            }
            if (!formData.codeName.trim()) {
                notifyError("Le code est requis.");
                return;
            }
            if (formData.id) {
                await editGroup.put(formData);
            } else {
                await saveGroup.post(formData);
            }
            fetchData();
            setFormData(clearFormData());
            notify('Enregistrer avec success !');
            setModalShow(false);
        } catch (error) {
            notifyError("Erreur lors de l'enregistrement.")
        } finally {
            setIsSaving(false);
            submitLockRef.current = false;
        }
    }

    const handleValidateName = async () => {
        const codeName = (formData?.codeName ?? '').toString();
        if (!formData.name.trim()) {
            notifyError("Le nom est requis.");
            return;
        }
        if (!codeName.trim()) {
            notifyError("Le code est requis.");
            return;
        }
        try {
            if (formData.id) {
                await editNameGroup.put(formData);
            }
            fetchData();
            setFormData(clearFormData());
            notify('Enregistrer avec success !');
            setModalUpdateShow(false);
        } catch (error) {
            notifyError("Erreur lors de l'enregistrement.\nCode doit être unique.")
        }
    }

    const handleEdit = (item: any) => {
        setFormData({
            id: item.id,
            name: item.name,
            admin_id: [],
            adminName: "",
            codeName: item.codeName
        });
        setModalUpdateShow(true);
    }

    const options = Array.isArray(userList)
        ? userList.map(user => ({
            value: user.id.toString(),
            label: `${user.firstName} ${user.lastName}`,
        }))
        : [];


    return (
        <div className="custom-content">
            <div className="custom-header">
                <span className="custom-title">Liste des groupes</span>
                <button className="btn-add-circle" onClick={() => setModalShow(true)}>
                    <span className="btn-text">Ajouter un groupe</span>
                    <span className="plus-icon-circle"><img src={plusCircle} alt="icone +" /></span>
                </button>
            </div>

            <ToastContainer />

            <FilterComponent
                filterText={filterText}
                onFilter={handleFilter}
                onClear={handleClear}
            />

            <TableContainer>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    paginationResetDefaultPage={resetPaginationToggle}
                    conditionalRowStyles={conditionalRowStyles}
                    persistTableHead
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 150px)"
                    progressPending={pending}
                    progressComponent={<CustomLoader />}
                    customStyles={customHeader}
                    paginationComponentOptions={paginationOptions}
                    noDataComponent={<p style={{ marginTop: "20px" }}>Aucun enregistrement trouvé.</p>}
                />
            </TableContainer>

            <FormModal
                show={modalUpdateShow}
                onValidate={handleValidateName}
                onAbort={() => {
                    if (isSaving) return;
                    setModalUpdateShow(false);
                    setFormData(clearFormData());
                }}
                title={`Détails du groupe`}
                isBusy={isSaving}
                btnValidate={isSaving ? "Enregistrement..." : "Enregistrer"}
                abortDisabled={isSaving}
                body={
                    <>
                        <Form>
                            <Form.Group className="mb-3" controlId="group.ControlInput">
                                <Form.Label>Nom du groupe</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Nom"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    autoFocus
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Code"
                                    name="codeName"
                                    value={formData.codeName}
                                    onChange={(e) => {
                                        const value = e.target.value.toUpperCase().slice(0, 4);
                                        setFormData({ ...formData, codeName: value });
                                    }}
                                    maxLength={4}
                                    style={{ textTransform: 'uppercase' }}
                                    required
                                />
                            </Form.Group>
                        </Form>
                    </>
                }
            />

            <FormModal
                show={modalShow}
                onValidate={handleValidate}
                onAbort={() => {
                    setModalShow(false);
                    setFormData(clearFormData());
                }}
                title={`Détails du groupe`}
                body={
                    <>
                        <Form>
                            <Form.Group className="mb-3" controlId="group.ControlInput">
                                <Form.Label>Nom du groupe</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Nom"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    autoFocus
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Code"
                                    name="codeName"
                                    value={formData.codeName}
                                    onChange={(e) => {
                                        const value = e.target.value.toUpperCase().slice(0, 4);
                                        setFormData({ ...formData, codeName: value });
                                    }}
                                    maxLength={4}
                                    style={{ textTransform: 'uppercase' }}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" >
                                <Form.Label>Administrateur du groupe</Form.Label>
                                <MultiSelectDropdown
                                    options={options}
                                    value={formData.admin_id}
                                    onChange={(values) => setFormData({ ...formData, admin_id: values })}
                                />
                            </Form.Group>
                        </Form>
                    </>
                }
            />
        </div>
    );
};

export default UserGroup;